import { useEffect, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Button from 'components/Button/Button';
import Modal from 'components/Modal/Modal';
import Select from 'components/Select/Select';
import Textarea from 'components/Textarea/Textarea';
import {
  IMPACT_STATUS,
  IRO_CATEGORY, IRO_DESCRIPTION, NACE_CODE, NACE_SECTION, POSITION_ON_VALUE_CHAINS, REPORTING_FRAMEWORK,
  TOPIC,
} from 'constants/formFields';
import {
  DEFAULT_MAX_LIMITED, DEFAULT_OPTION_OPPORTUNITY, IMPACT_STATUS_OPTIONS, IRO_CATEGORY_OPTIONS, IRO_VC_POSITION_OPTIONS,
} from 'constants/general';
import { BUTTON_VARIANT } from 'constants/interfaces';
import { useGetNaceCodeTreeQuery } from 'store/slices/dataSets/apis/dataSetsApi';
import { selectNaceCodes } from 'store/slices/dataSets/selectors';
import { useGetStandardTopicsListQuery } from 'store/slices/standards/apis/standardsApi';

import useCreateInvestorForm from './hooks/useCreateIROForm';

import type IOption from 'components/MappingModal/interfaces/IOption';
import type IFieldError from 'store/types/IFieldError';
import type ICreateCustomIROItem from './interfaces/ICreateCustomIROItem';

import styles from './scss/CreateCustomIROItem.module.scss';

function CreateCustomIROItem({
  open, title, handleCancel, createIROItem, standards,
  selectEditItem, naceCodeTreeData, updateIROItem, opportunity,
}: ICreateCustomIROItem) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const [divisions, setDivisions] = useState<IOption[]>([]);

  const { data: naceCodesData, isLoading: isNaceCodesTreeLoading } = useGetNaceCodeTreeQuery(undefined, {
    selectFromResult: (result) => ({ ...result, data: selectNaceCodes(result.data) }),
  });

  const isLoading = isNaceCodesTreeLoading;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
  } = useCreateInvestorForm({
    isPageLoading: isLoading,
    selectedItem: selectEditItem,
    naceCodeTreeData,
    open,
  });

  useEffect(() => {
    if (opportunity) {
      setValue(IMPACT_STATUS, DEFAULT_OPTION_OPPORTUNITY);
    }
  }, [opportunity, setValue, title]);

  const { data: standardTopics } = useGetStandardTopicsListQuery({
    offset: 0,
    standardId: watch(REPORTING_FRAMEWORK)?.value ? Number(watch(REPORTING_FRAMEWORK)?.value) : 0,
  });

  const handleCancelModal = () => {
    handleCancel();
    reset();
  };

  const onCreate = handleSubmit((data) => {
    const body = {
      description: data[IRO_DESCRIPTION] || '',
      impact: data[IMPACT_STATUS]?.value || '',
      impactType: data[IRO_CATEGORY]?.value || '',
      valueChainSection: data[POSITION_ON_VALUE_CHAINS]?.value || '',
      standardId: data[REPORTING_FRAMEWORK]?.value ? Number(data[REPORTING_FRAMEWORK]?.value) : 0,
      topicId: data[TOPIC]?.value ? Number(data[TOPIC]?.value) : 0,
      naceSection: data[NACE_SECTION]?.value || '',
      naceCode: data[NACE_CODE]?.value || '',
    };
    if (selectEditItem) {
      updateIROItem?.(body);
    } else {
      createIROItem?.(body);
    }
    handleCancelModal();
  });

  const onNaceSelectionChange = () => {
    setValue(NACE_CODE, {});
  };

  useEffect(() => {
    if (watch(NACE_SECTION).value) {
      const naceDivisions = (naceCodesData.find((item) => item.value === watch(NACE_SECTION).value)?.divisions || []);
      setDivisions(naceDivisions);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue, watch(NACE_SECTION), naceCodesData, watch]);

  const standardsOptions = standards?.map((standard) => ({
    label: standard.name,
    value: standard.id,
  })) || [];

  const topicOptions = standardTopics?.data?.items?.map((topic) => ({
    label: topic.title,
    value: topic.id,
  })) || [];

  return (
    <Modal
      open={open}
      title={title || ''}
      className={styles.modalContainer}
      isButtonsVisible={false}
    >
      <div className={styles.root} ref={modalRef}>
        <div className={styles.formWrapper}>
          <Controller
            control={control}
            name={NACE_SECTION}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.industryCode')}
                placeholder={t('materialityAssessment.placeholder.selectIndustry')}
                options={naceCodesData}
                value={value?.value ? value : null}
                onChange={
                  (newValue) => {
                    onChange(newValue);
                    onNaceSelectionChange();
                  }
                }
                error={(error as IFieldError)?.message}
                modalRef={modalRef}
                menuPosition="absolute"
                required
              />
            )}
          />
          <Controller
            control={control}
            name={NACE_CODE}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.sector')}
                placeholder={t('materialityAssessment.placeholder.selectSector')}
                options={divisions}
                value={value?.value ? value : null}
                onChange={onChange}
                error={(error as IFieldError)?.message}
                modalRef={modalRef}
                menuPosition="absolute"
                required
              />
            )}
          />
          <Controller
            control={control}
            name={REPORTING_FRAMEWORK}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.reportingFramework')}
                placeholder={t('materialityAssessment.placeholder.reportingFramework')}
                options={standardsOptions}
                value={value?.value ? value : null}
                onChange={onChange}
                error={(error as IFieldError)?.message}
                modalRef={modalRef}
                menuPosition="absolute"
                required
              />
            )}
          />
          <Controller
            control={control}
            name={TOPIC}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.topic')}
                placeholder={t('materialityAssessment.placeholder.topic')}
                options={topicOptions}
                value={value?.value ? value : null}
                onChange={onChange}
                error={(error as IFieldError)?.message}
                modalRef={modalRef}
                menuPosition="absolute"
                isDisabled={!watch(REPORTING_FRAMEWORK).value}
                required
              />
            )}
          />
          <Controller
            control={control}
            name={IRO_CATEGORY}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.iroCategory')}
                placeholder={t('materialityAssessment.placeholder.iroCategory')}
                options={IRO_CATEGORY_OPTIONS}
                value={value?.value ? value : null}
                onChange={onChange}
                error={(error as IFieldError)?.message}
                modalRef={modalRef}
                menuPosition="absolute"
                required
              />
            )}
          />
          <Controller
            control={control}
            name={IRO_DESCRIPTION}
            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
              <Textarea
                name={name}
                value={value}
                label={t('materialityAssessment.fields.iroDescription')}
                error={error?.message}
                onChange={(event) => onChange(event.target.value)}
                placeholder={t('materialityAssessment.placeholder.typeHere')}
                charactersLimit={DEFAULT_MAX_LIMITED}
                className={styles.textarea}
                required
                hideCounter
              />
            )}
          />
          <Controller
            control={control}
            name={IMPACT_STATUS}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.impactStatus')}
                placeholder={t('materialityAssessment.placeholder.selectImpactStatus')}
                options={IMPACT_STATUS_OPTIONS}
                value={value?.value ? value : null}
                onChange={onChange}
                error={(error as IFieldError)?.message}
                modalRef={modalRef}
                menuPosition="absolute"
                isDisabled={opportunity}
                required
              />
            )}
          />
          <Controller
            control={control}
            name={POSITION_ON_VALUE_CHAINS}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.positionOnValueChains')}
                placeholder={t('materialityAssessment.placeholder.positionOnValueChains')}
                options={IRO_VC_POSITION_OPTIONS}
                value={value?.value ? value : null}
                onChange={onChange}
                error={(error as IFieldError)?.message}
                modalRef={modalRef}
                menuPosition="absolute"
                required
              />
            )}
          />
        </div>
        <div className={styles.buttonWrapper}>
          <Button
            title={t('common.cancel')}
            variant={BUTTON_VARIANT.cancel}
            className={styles.button}
            onClick={handleCancelModal}
          />
          <Button
            title={selectEditItem ? t('common.edit') : t('common.add')}
            onClick={onCreate}
            className={styles.button}
          />
        </div>
      </div>
    </Modal>
  );
}

export default CreateCustomIROItem;
